/**
 * Campaign metrics spreadsheet.
 *
 * One row per campaign that RAN during the selected date window (flight overlaps it), with the delivery
 * metrics as columns. The window defaults to LAST calendar month and scopes both the campaign list and the
 * figures. Impressions, Clicks, and the two Retargeting figures are editable per row (for a manual sheet /
 * export); CTR = clicks ÷ contracted and updates live as Clicks are edited. Row checkboxes choose what the
 * CSV export includes. Pagination sits at top and bottom with a 50 / 100 / 250 / 500 / ALL page size.
 *
 * The page-size options exceed the server's per-request caps (campaign list 300, metrics 200 ids), so a
 * logical page is assembled from chunked requests and sliced client-side.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hierarchy, ApiError, type Campaign, type CampaignMetrics, type ClientOption } from '../lib/api';

const PAGE_SIZES = [50, 100, 250, 500, 'ALL'] as const;
type PageSize = (typeof PAGE_SIZES)[number];
const CHUNK = 200; // server-side fetch size, within both the campaign-list (300) and metrics (200) caps

const nf = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString('en-US'));
const day = (v: string | null) => (v ? v.slice(0, 10) : '—');
const ctrPct = (clicks: number, contracted: number | null): number | null =>
  contracted && contracted > 0 ? Math.round((clicks / contracted) * 100 * 1e4) / 1e4 : null;
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** The full previous calendar month, e.g. 2026-08-01 … 2026-08-31 when today is in September 2026. */
function lastMonth(): { start: string; end: string } {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), 0);      // day 0 of this month = last day of prev
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  return { start: ymd(start), end: ymd(end) };
}

const ZERO: CampaignMetrics = { contractedImpressions: null, impressionsSim: 0, impressionsActual: 0, clicks: 0, retargetImpressions: 0, retargetClicks: 0 };

interface Edited { impressions: number; clicks: number; retargetImpressions: number; retargetClicks: number }
interface Row { campaign: Campaign; metrics: CampaignMetrics; edited: Edited; selected: boolean }
interface Applied { query: string; start: string; end: string }

export function SpreadsheetScreen(): JSX.Element {
  const initial = lastMonth();
  const [search, setSearch] = useState('');
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [applied, setApplied] = useState<Applied>({ query: '', ...initial });
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const [page, setPage] = useState(1);
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);

  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (p: number, size: PageSize, a: Applied, cids: string[]) => {
    setLoading(true); setError(null);
    try {
      const limit = size === 'ALL' ? null : size;
      const offset = limit === null ? 0 : (p - 1) * limit;

      // Assemble the logical page from CHUNK-sized server requests, then slice.
      const startServerRow = Math.floor(offset / CHUNK) * CHUNK;
      const needEnd = limit === null ? Infinity : offset + limit;
      const fetched: Campaign[] = [];
      let serverPage = startServerRow / CHUNK + 1;
      let seen = startServerRow;
      let grandTotal = 0;
      while (seen < needEnd) {
        const list = await hierarchy.campaigns({
          page: serverPage, pageSize: CHUNK, search: a.query || undefined, ranFrom: a.start, ranTo: a.end,
          clientIds: cids.length ? cids : undefined,
          // Eblast (email) campaigns belong on the /eblast screen, not the RTB metrics spreadsheet.
          excludeEblast: true,
        });
        grandTotal = list.total;
        fetched.push(...list.rows);
        seen += list.rows.length;
        serverPage += 1;
        if (list.rows.length < CHUNK) break; // last page
      }
      const sliceStart = offset - startServerRow;
      const camps = limit === null ? fetched.slice(sliceStart) : fetched.slice(sliceStart, sliceStart + limit);

      // Metrics for exactly those campaigns, chunked to the 200-id cap.
      const metrics: Record<string, CampaignMetrics> = {};
      const ids = camps.map((c) => c.id);
      for (let i = 0; i < ids.length; i += 200) {
        Object.assign(metrics, await hierarchy.campaignMetrics(ids.slice(i, i + 200), { start: a.start, end: a.end }));
      }

      setRows(camps.map((c) => {
        const m = metrics[c.id] ?? ZERO;
        return {
          campaign: c, metrics: m, selected: true,
          edited: { impressions: m.impressionsSim, clicks: m.clicks, retargetImpressions: m.retargetImpressions, retargetClicks: m.retargetClicks },
        };
      }));
      setTotal(grandTotal);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load campaign metrics.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(page, pageSize, applied, clientIds); }, [load, page, pageSize, applied, clientIds]);

  // The client filter offers only clients present in the current window; refresh options when it changes.
  useEffect(() => {
    hierarchy.campaignClients({ ranFrom: applied.start, ranTo: applied.end, search: applied.query || undefined })
      .then(setClientOptions).catch(() => setClientOptions([]));
  }, [applied]);

  const apply = () => { setPage(1); setClientIds([]); setApplied({ query: search, start, end }); };
  const onClients = (ids: string[]) => { setPage(1); setClientIds(ids); };
  const patch = (id: string, field: keyof Edited, value: number) =>
    setRows((rs) => rs.map((r) => (r.campaign.id === id ? { ...r, edited: { ...r.edited, [field]: value } } : r)));
  const setSelected = (id: string, sel: boolean) =>
    setRows((rs) => rs.map((r) => (r.campaign.id === id ? { ...r, selected: sel } : r)));
  const setAllSelected = (sel: boolean) => setRows((rs) => rs.map((r) => ({ ...r, selected: sel })));

  const pages = pageSize === 'ALL' ? 1 : Math.max(1, Math.ceil(total / pageSize));
  const allChecked = rows.length > 0 && rows.every((r) => r.selected);
  const selectedCount = rows.filter((r) => r.selected).length;

  const csv = useMemo(() => () => {
    const head = ['SO', 'Client Name', 'Campaign Name', 'Create Date', 'Start Date', 'End Date', 'Contracted Impressions', 'Actual Impressions', 'Retargeting Impressions', 'Total Clicks', 'Retargeting Clicks', 'CTR %'];
    const cell = (v: string | number) => {
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [head.join(',')];
    // Export sorted by create date (oldest first); missing dates sort last. ISO strings sort chronologically.
    const selected = rows
      .filter((r) => r.selected)
      .sort((a, b) => (a.campaign.createDatetime ?? '￿').localeCompare(b.campaign.createDatetime ?? '￿'));
    for (const r of selected) {
      const ctr = ctrPct(r.edited.clicks, r.metrics.contractedImpressions);
      lines.push([
        r.campaign.so, r.campaign.clientName, r.campaign.name,
        day(r.campaign.createDatetime), day(r.campaign.startDatetime), day(r.campaign.endDatetime),
        r.metrics.contractedImpressions ?? '', r.edited.impressions, r.edited.retargetImpressions,
        r.edited.clicks, r.edited.retargetClicks, ctr ?? '',
      ].map(cell).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `campaign-metrics-${applied.start}_to_${applied.end}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }, [rows, applied]);

  const Pager = () => (
    <div className="sheet-pager">
      <label className="rng">Per page
        <select value={String(pageSize)} onChange={(e) => { setPage(1); setPageSize(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value) as PageSize); }}>
          {PAGE_SIZES.map((s) => <option key={String(s)} value={String(s)}>{s}</option>)}
        </select>
      </label>
      <span className="muted">{total.toLocaleString('en-US')} campaigns</span>
      <div className="grow" />
      <button className="button" disabled={pageSize === 'ALL' || page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Prev</button>
      <span className="muted">Page {page} / {pages}</span>
      <button className="button" disabled={pageSize === 'ALL' || page >= pages || loading} onClick={() => setPage((p) => Math.min(pages, p + 1))}>Next ›</button>
    </div>
  );

  return (
    <div className="sheet-screen wide">
      <div className="sheet-bar">
        <h1>Campaign metrics</h1>
        <form className="sheet-controls" onSubmit={(e) => { e.preventDefault(); apply(); }}>
          <label className="rng">From<input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label className="rng">To<input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
          <input className="sheet-searchbox" placeholder="Search campaigns…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button className="button" type="submit">Apply</button>
        </form>
        <ClientMultiSelect options={clientOptions} value={clientIds} onChange={onClients} />
        <div className="grow" />
        <button className="button" onClick={() => csv()} disabled={!selectedCount}>Export CSV{selectedCount ? ` (${selectedCount})` : ''}</button>
      </div>
      <p className="sheet-note muted">
        Campaigns that went live between <strong>{applied.start}</strong> and <strong>{applied.end}</strong>; figures are their delivery through <strong>{applied.end}</strong>. Editable cells and the export are for building a manual sheet — they do not change stored data.
      </p>

      {error && <div className="banner error">{error}</div>}

      <Pager />

      <div className="sheet-wrap">
        <table className="sheet">
          <thead>
            <tr>
              <th className="chk"><input type="checkbox" checked={allChecked} onChange={(e) => setAllSelected(e.target.checked)} aria-label="Select all" /></th>
              <th>Campaign Name</th><th>SO</th><th>Start</th><th>End</th>
              <th className="num">Contracted</th>
              <th className="num impr-col">Impressions</th>
              <th className="num">Clicks</th><th className="num">CTR</th>
              <th className="num">Retarget Impr.</th><th className="num">Retarget Clicks</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="sheet-empty">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="sheet-empty">No campaigns went live during this window.</td></tr>
            ) : rows.map((r) => {
              const m = r.metrics;
              const ctr = ctrPct(r.edited.clicks, m.contractedImpressions);
              const isSim = r.edited.impressions === m.impressionsSim;
              const isAct = r.edited.impressions === m.impressionsActual;
              return (
                <tr key={r.campaign.id} className={r.selected ? '' : 'unsel'}>
                  <td className="chk"><input type="checkbox" checked={r.selected} onChange={(e) => setSelected(r.campaign.id, e.target.checked)} aria-label="Select row" /></td>
                  <td className="name">{r.campaign.name}</td>
                  <td className="mono">{r.campaign.so || '—'}</td>
                  <td className="mono">{day(r.campaign.startDatetime)}</td>
                  <td className="mono">{day(r.campaign.endDatetime)}</td>
                  <td className="num">{nf(m.contractedImpressions)}</td>
                  <td className="num impr-col">
                    <input className="edit num" type="number" value={r.edited.impressions}
                      onChange={(e) => patch(r.campaign.id, 'impressions', Math.max(0, Math.floor(Number(e.target.value) || 0)))} />
                    <div className="impr-links">
                      <button type="button" className={`lnk${isSim ? ' on' : ''}`} title="Use the simulated value"
                        onClick={() => patch(r.campaign.id, 'impressions', m.impressionsSim)}>Sim {nf(m.impressionsSim)}</button>
                      <button type="button" className={`lnk${isAct ? ' on' : ''}`} title="Use the actual value"
                        onClick={() => patch(r.campaign.id, 'impressions', m.impressionsActual)}>Actual {nf(m.impressionsActual)}</button>
                    </div>
                  </td>
                  <td className="num">
                    <input className="edit num" type="number" value={r.edited.clicks}
                      onChange={(e) => patch(r.campaign.id, 'clicks', Math.max(0, Math.floor(Number(e.target.value) || 0)))} />
                  </td>
                  <td className="num">{ctr === null ? '—' : `${ctr.toFixed(2)}%`}</td>
                  <td className="num">
                    <input className="edit num" type="number" value={r.edited.retargetImpressions}
                      onChange={(e) => patch(r.campaign.id, 'retargetImpressions', Math.max(0, Math.floor(Number(e.target.value) || 0)))} />
                  </td>
                  <td className="num">
                    <input className="edit num" type="number" value={r.edited.retargetClicks}
                      onChange={(e) => patch(r.campaign.id, 'retargetClicks', Math.max(0, Math.floor(Number(e.target.value) || 0)))} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pager />
    </div>
  );
}

/**
 * A compact multi-select for the client filter — offers only the clients present in the current window.
 * Selecting narrows the grid; empty means all clients. Filterable, with All / None.
 */
function ClientMultiSelect({ options, value, onChange }: {
  options: ClientOption[]; value: string[]; onChange: (ids: string[]) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const shown = options.filter((o) => o.name.toLowerCase().includes(filter.trim().toLowerCase()));
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const label = value.length === 0 ? 'All clients' : `${value.length} selected`;

  return (
    <div className="msel" ref={ref}>
      <button type="button" className="button msel-btn" onClick={() => setOpen((o) => !o)} disabled={options.length === 0}>
        Clients: {label} <span className="caret">▾</span>
      </button>
      {open && (
        <div className="msel-panel">
          <input className="msel-filter" placeholder="Filter clients…" value={filter} onChange={(e) => setFilter(e.target.value)} autoFocus />
          <div className="msel-actions">
            {/* All / None act on the MATCHED (filtered) clients only — add them to, or remove them from,
                the current selection, so a search + All/None never touches clients you cannot see. */}
            <button type="button" className="lnk"
              onClick={() => onChange([...new Set([...value, ...shown.map((o) => o.id)])])}>All</button>
            <button type="button" className="lnk"
              onClick={() => { const s = new Set(shown.map((o) => o.id)); onChange(value.filter((id) => !s.has(id))); }}>None</button>
            <span className="grow" />
            <span className="muted">{value.length}/{options.length}</span>
          </div>
          <div className="msel-list">
            {shown.length === 0 ? <div className="muted msel-empty">No matching clients.</div>
              : shown.map((o) => (
                <label key={o.id} className="msel-item">
                  <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} />
                  <span>{o.name}</span>
                </label>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
